import type { Data } from '../types';

const ANALYZE_URL = process.env.REACT_APP_API_URL ?? '/analyze';

type AnalysisResponse = {
  result: Data;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isData = (value: unknown): value is Data =>
  isRecord(value) && isRecord(value.playerstats);

const parseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

const readErrorMessage = (payload: unknown, status: number): string => {
  if (isRecord(payload) && typeof payload.error === "string") {
    return payload.error;
  }
  if (typeof payload === "string" && payload) {
    return payload;
  }
  return `Request failed with ${status}`;
};

export async function analyzeLogFile(file: File): Promise<Data> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(ANALYZE_URL, {
    method: 'POST',
    body: formData,
  });

  const payload = await parseBody(response);

  if (!response.ok) {
    throw new Error(readErrorMessage(payload, response.status));
  }

  if (!isRecord(payload) || !('result' in payload)) {
    throw new Error('Unexpected response shape');
  }

  const result = (payload as AnalysisResponse).result;
  if (!isData(result)) {
    throw new Error('Unexpected response shape');
  }

  return result;
}
